import React, { createContext, useContext, useState, useMemo } from 'react';
import { useChat } from '@tanstack/ai-react';
import { fetchServerSentEvents } from '@tanstack/ai-client';

interface AppContextType {
    selectedDate: Date;
    setSelectedDate: (date: Date) => void;
    selectedBranchId: string | null;
    setSelectedBranchId: (id: string) => void;
    chat: ReturnType<typeof useChat> & {
        input: string;
        setInput: (value: string) => void;
    };
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

    // Chat Persistence
    // We use a ref to access current state inside the body callback without recreating the connection constantly
    // if the library supports dynamic body generation.
    // However, fetchServerSentEvents might bind the body function once.
    // To be safe and simple, let's include them in the closure and recreate if they change significantly?
    // Actually, using refs is better to avoid reconnects on every date change if not needed.
    const contextRef = React.useRef({ selectedDate, selectedBranchId });

    // Update ref when state changes
    React.useEffect(() => {
        contextRef.current = { selectedDate, selectedBranchId };
    }, [selectedDate, selectedBranchId]);

    const connection = useMemo(() => fetchServerSentEvents('/api/chat', {
        headers: {
            'Content-Type': 'application/json',
            'x-tenant-slug': 'demo-library',
            'x-selected-date': selectedDate.toISOString(),
            'x-selected-branch-id': selectedBranchId || '',
        },
        body: (messages: any[]) => {
            const simpleMessages = messages.map((msg: any) => {
                const text = msg.parts
                    .filter((p: any) => p.type === 'text')
                    .map((p: any) => p.content).join('');
                return { role: msg.role, content: text };
            });

            const bodyData = {
                messages: simpleMessages,
                context: {
                    selectedDate: selectedDate.toISOString(),
                    selectedBranchId: selectedBranchId,
                }
            };
            return JSON.stringify(bodyData);
        }
    }), [selectedDate, selectedBranchId]);

    const chat = useChat({
        connection,
    });

    // Manually manage input state for persistence across tabs
    const [input, setInput] = useState('');

    return (
        <AppContext.Provider value={{
            selectedDate,
            setSelectedDate,
            selectedBranchId,
            setSelectedBranchId,
            chat: { ...chat, input, setInput }
        }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
}
